import { Component, signal, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AuthenticationService } from '../../../core/services/Authentication/authentication.service';
import { TicketService } from '../../../core/services/ticket.service';

@Component({
  selector: 'app-guest-welcome-modal',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (isOpen()) {
      <div
        (click)="closeModal()"
        class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm animate-in fade-in duration-200">
        
        <div
          (click)="$event.stopPropagation()"
          class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl max-w-lg w-full p-6 sm:p-7 shadow-2xl space-y-6 transform transition-all scale-100 relative">
          
          <!-- Close button -->
          <button
            (click)="closeModal()"
            type="button"
            class="absolute top-4 right-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
            title="Close">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <!-- Header / Icon & Badge -->
          <div class="flex items-start space-x-4">
            <div class="p-3.5 bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-800/60 rounded-2xl shrink-0 text-blue-600 dark:text-blue-400 shadow-xs">
              <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            
            <div class="space-y-1 pr-6">
              <div class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800/50">
                <span class="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                Guest Access Active
              </div>
              <h3 class="text-lg font-bold text-slate-900 dark:text-slate-100">
                Welcome to Savinda Support Desk
              </h3>
              <p class="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                You are currently signed in as a <strong class="text-slate-700 dark:text-slate-300">Guest</strong>.
              </p>
            </div>
          </div>

          <!-- Feature Highlights Card -->
          <div class="bg-slate-50 dark:bg-slate-950/60 border border-slate-200/80 dark:border-slate-800/80 rounded-xl p-4 space-y-2.5">
            <h4 class="text-[11px] font-bold tracking-wider uppercase text-slate-500 dark:text-slate-400">
              What you can do as Guest
            </h4>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-600 dark:text-slate-300">
              <div class="flex items-center space-x-2">
                <svg class="w-4 h-4 text-emerald-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/>
                </svg>
                <span>Browse public tickets</span>
              </div>
              <div class="flex items-center space-x-2">
                <svg class="w-4 h-4 text-emerald-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/>
                </svg>
                <span>View project directories</span>
              </div>
              <div class="flex items-center space-x-2">
                <svg class="w-4 h-4 text-emerald-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/>
                </svg>
                <span>Create & track tickets</span>
              </div>
              <div class="flex items-center space-x-2">
                <svg class="w-4 h-4 text-emerald-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/>
                </svg>
                <span>Add comments & files</span>
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5 pt-2 border-t border-slate-100 dark:border-slate-800/80">
            <button
              (click)="openAccountRequestModal()"
              type="button"
              class="px-3.5 py-2 text-xs font-bold text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/40 rounded-xl transition-colors text-left flex items-center space-x-1.5 cursor-pointer">
              <svg class="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              <span>Request Dedicated Account</span>
            </button>
            <div class="flex items-center space-x-2">
              <button
                (click)="closeModal()"
                type="button"
                class="px-5 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-500 active:bg-blue-700 rounded-xl transition-all shadow-md shadow-blue-600/20 text-center cursor-pointer">
                Continue as Guest
              </button>
            </div>
          </div>

        </div>
      </div>
    }
  `,
})
export class GuestWelcomeModalComponent implements OnInit {
  private readonly authService = inject(AuthenticationService);
  private readonly ticketService = inject(TicketService);
  private readonly SESSION_KEY = 'guest_welcome_modal_dismissed';

  readonly isOpen = signal<boolean>(false);

  ngOnInit(): void {
    this.checkGuestStatus();
  }

  checkGuestStatus(): void {
    const isDismissed = sessionStorage.getItem(this.SESSION_KEY);
    if (isDismissed) return;

    const user = this.authService.currentUser();
    const isGuest =
      user?.username === 'guest' ||
      user?.name === 'Guest User' ||
      user?.id === '1' ||
      user?.id === 1 as any;

    if (isGuest) {
      setTimeout(() => {
        this.isOpen.set(true);
      }, 400);
    }
  }

  openModal(): void {
    this.isOpen.set(true);
  }

  openAccountRequestModal(): void {
    this.closeModal();
    this.ticketService.openGuestAccountRequestTicket();
  }

  closeModal(): void {
    this.isOpen.set(false);
    sessionStorage.setItem(this.SESSION_KEY, 'true');
  }
}
